// Politie en handhaving: door rood rijden en katten aanrijden levert een achtervolging en een boete op.
// Methoden worden op BezorgerGame.prototype gezet (zie game/mixins.js).

import { t } from '../i18n';

// De bedragen staan hier, de teksten (fine.<soort>.*) in i18n/nl.ts en en.ts.
const FINES = {
  rood_licht: { amount: 40, amounts: [35, 5] },
  kat: { amount: 45, amounts: [35, 10] },
};

/** De boete in de huidige taal, klaar voor het boetescherm. */
function fineText(reason, catName) {
  const f = FINES[reason] ?? FINES.kat;
  const kind = FINES[reason] ? reason : 'kat';
  return {
    amount: f.amount,
    title: kind === 'kat' && catName ? t('fine.kat.titleNamed', { cat: catName }) : t(`fine.${kind}.title`),
    lines: f.amounts.map((a, i) => [t(`fine.${kind}.line${i + 1}`), a]),
    quote: t(`fine.${kind}.quote`),
  };
}

export class PoliceMixin {
  resetPolice() {
    this.policeActive = false;
    this.policeImmunityTimer = 0;
    this.policeFineModalOpen = false;
    this.policeDispatchTimer = 0;
    this.cameraFlashTimer = 0;
    this.pendingFine = null;
  }

  triggerCatAccident(cat) {
    // In de geleide eerste minuut gaat een aanrijding zonder politie en boete
    if (this.guided) {
      this.sound.playCatScreech();
      return;
    }
    this.policeImmunityTimer = 0;
    this.sound.playCatScreech();
    this.sound.startPoliceSiren();
    this.policeActive = true;
    this.lastHitCat = cat?.name ?? null;
    this.ui?.alert(t('fine.hitCat', { cat: this.lastHitCat ?? t('fine.neighbourCat') }));
    this.dispatchPolice(4.2);
  }

  /** Stuurt een politiewagen achter de speler aan, desnoods vlak achter hem. */
  dispatchPolice(speed) {
    if (!this.map) return;
    this.map.cars = this.map.cars ?? [];
    const police = this.map.cars.filter((c) => c.isPolice);
    let nearest = null;
    let nearestDist = Infinity;
    for (const p of police) {
      const d = Math.hypot(p.x - this.scooter.x, p.y - this.scooter.y);
      if (d < nearestDist) {
        nearestDist = d;
        nearest = p;
      }
    }
    if (!nearest || nearestDist > 550) {
      const a = this.scooter.angle + Math.PI + (Math.random() - 0.5) * 0.6;
      const x = Math.max(100, Math.min(this.map.width - 100, this.scooter.x + Math.cos(a) * 300));
      const y = Math.max(100, Math.min(this.map.height - 100, this.scooter.y + Math.sin(a) * 300));
      if (nearest) {
        Object.assign(nearest, { x, y, angle: Math.atan2(this.scooter.y - y, this.scooter.x - x), speed });
      } else {
        this.map.cars.push({
          id: `politie_${Date.now()}`,
          name: 'Politie',
          road: 'Stadsader',
          isPolice: true,
          x,
          y,
          angle: Math.atan2(this.scooter.y - y, this.scooter.x - x),
          speed,
          targetSpeed: 5.2,
          length: 76,
          width: 36,
          color: '#ffffff',
          roofColor: '#ffffff',
          model: 'police',
          isBraking: false,
          hornCooldown: 0,
          flashTimer: 0.8,
          hazardTimer: 0,
          hazardBlink: false,
          turnSignal: null,
          turnTimer: 0,
          speechText: '🚨 "Politie! Stop aan de kant!"',
          speechTimer: 4,
          playerBlockTimer: 0,
          reactionDelayTimer: 0,
          sirenActive: true,
          sirenTimer: 0,
          state: 'chase',
        });
      }
    }
    for (const car of this.map.cars) {
      if (!car.isPolice) continue;
      car.state = 'chase';
      car.sirenActive = true;
      car.targetSpeed = 5.2;
    }
  }

  /** Houdt de achtervolging gaande: zit er geen agent in de buurt, dan wordt er een gestuurd. */
  updatePolice(dt) {
    if (this.policeImmunityTimer > 0) this.policeImmunityTimer -= dt;
    if (this.cameraFlashTimer > 0) this.cameraFlashTimer -= dt;
    if (!this.policeActive || this.policeFineModalOpen) return;
    this.sound.startPoliceSiren();
    this.policeDispatchTimer = (this.policeDispatchTimer || 0) + dt;
    if (this.policeDispatchTimer < 3) return;
    this.policeDispatchTimer = 0;
    const near = (this.map.cars ?? []).some(
      (c) => c.isPolice && c.state === 'chase' && Math.hypot(c.x - this.scooter.x, c.y - this.scooter.y) < 750,
    );
    if (!near) this.dispatchPolice(4.5);
  }

  /** Een agent heeft de speler ingehaald: boete. */
  handlePoliceIntercept(policeCar, reason = 'kat') {
    if (this.policeFineModalOpen || this.policeImmunityTimer > 0) return;
    this.policeFineModalOpen = true;
    this.scooter.speed = 0;
    this.releaseControls();
    this.sound.stopPoliceSiren();
    this.sound.playPoliceWhistle();
    for (const car of this.map.cars ?? []) {
      if (car.isPolice) {
        car.state = 'stopped';
        car.sirenActive = false;
      }
    }
    this.pendingFine = { reason, ...fineText(reason, this.lastHitCat) };
    this.ui?.showFine(this.pendingFine);
  }

  /** Betaal de boete. Met een goed verhaal (45% kans) wordt hij gehalveerd. */
  payPoliceFine(contest = false) {
    const fine = this.pendingFine ?? { amount: 40 };
    let amount = fine.amount;
    const halved = contest && Math.random() < 0.45;
    if (halved) amount = Math.round(amount / 2);
    // Een boete gaat van je portemonnee af (nooit onder nul) en telt als min in je dagresultaat
    this.save.wallet = Math.max(0, Math.round((this.save.wallet - amount) * 100) / 100);
    this.shift?.addEarnings(-amount);
    this.finedThisShift = true;
    this.persist();
    this.sound.playCashRegister();
    this.ui?.toast(
      contest ? (halved ? t('fine.halved', { amount }) : t('fine.notBelieved', { amount })) : t('fine.paid', { amount }),
      'warn',
    );
    this.policeActive = false;
    this.policeFineModalOpen = false;
    this.pendingFine = null;
    this.policeImmunityTimer = 20;
    // De agenten rijden weg: ze verdwijnen en komen na een paar seconden aan de rand van de stad weer op patrouille.
    // (Vroeger gingen ze op dezelfde plek verder, vlak naast je, en botsten ze soms meteen op je.)
    this.map.removePolice();
  }

  triggerRedLightViolation(inter) {
    if (this.policeFineModalOpen || this.policeImmunityTimer > 0) return;
    this.sound.playCameraFlash();
    this.sound.playPoliceWhistle();
    this.cameraFlashTimer = 0.5;
    this.ui?.alert(t('fine.flash', { place: inter.name }));
    // Na de flits volgt de boete; de camera telt als staandehouding.
    this.redLightFineIn = 0.45;
  }

  checkRedLightViolations(dt) {
    if (this.redLightFineIn > 0) {
      this.redLightFineIn -= dt;
      if (this.redLightFineIn <= 0) this.handlePoliceIntercept(null, 'rood_licht');
      return;
    }
    if (this.policeImmunityTimer > 0 || this.policeFineModalOpen) return;
    if (!this.map?.intersections || Math.abs(this.scooter.speed) < 1) return;

    const s = this.scooter;
    const sx = s.x;
    const sy = s.y;
    const px = s.prevX ?? sx;
    const py = s.prevY ?? sy;
    const vx = sx - px;
    const vy = sy - py;
    const cos = Math.cos(s.angle);
    const sin = Math.sin(s.angle);

    for (const inter of this.map.intersections) {
      if (!inter.hasTrafficLight || !inter.trafficLight || inter.redViolatedTimer > 0) continue;
      const tl = inter.trafficLight;
      let violated = false;
      // Oostwaarts, westwaarts, zuidwaarts en noordwaarts over de stopstreep
      if ((cos > 0.25 || vx > 0.5) && px < inter.x - inter.hw && sx >= inter.x - inter.hw && sy >= inter.y - 12 && sy <= inter.y + inter.hh + 15) violated = tl.horizontal === 'red';
      if (!violated && (cos < -0.25 || vx < -0.5) && px > inter.x + inter.hw && sx <= inter.x + inter.hw && sy >= inter.y - inter.hh - 15 && sy <= inter.y + 12) violated = tl.horizontal === 'red';
      if (!violated && (sin > 0.25 || vy > 0.5) && py < inter.y - inter.hh && sy >= inter.y - inter.hh && sx >= inter.x - inter.hw - 15 && sx <= inter.x + 12) violated = tl.vertical === 'red';
      if (!violated && (sin < -0.25 || vy < -0.5) && py > inter.y + inter.hh && sy <= inter.y + inter.hh && sx >= inter.x - 12 && sx <= inter.x + inter.hw + 15) violated = tl.vertical === 'red';
      if (violated) {
        inter.redViolatedTimer = 12;
        this.triggerRedLightViolation(inter);
        return;
      }
    }
  }
}
